/**
 * Read an `.xlsx` export into the same `ParsedCsv` shape the CSV path produces — [B-134](../../../../docs/platform/TODO.md).
 *
 * ⚠️ **Why this matters more than it looks.** "Export your contacts" out of Salesforce or HubSpot
 * offers CSV *and* Excel, and plenty of people take the Excel one — it is the default in some
 * flows and the only thing a non-technical user recognises. A visitor whose first file is
 * rejected does not go and re-export; they leave. The local test drive is the one part of the
 * product that asks nothing of them, and it was refusing half the files they would naturally
 * bring.
 *
 * ─────────────────────────────────────────────────────────────────────────────────────────────
 * NO DEPENDENCY, AND THAT IS A DELIBERATE CHOICE RATHER THAN A SAVING
 *
 * An `.xlsx` is a ZIP of XML. `DecompressionStream('deflate-raw')` is in every browser this app
 * supports and in Node 18+, so the two things a reader needs — inflate and XML — are both
 * already present.
 *
 * The reason to avoid a spreadsheet library here is not bundle size. **This file runs in the one
 * place in the product that promises the data never leaves the device**, and every dependency
 * added to that path is code somebody has to keep trusting on a visitor's behalf. A parser for a
 * format this permissive is also a large attack surface: SheetJS has shipped prototype-pollution
 * and ReDoS advisories, and a malicious `.xlsx` is a file a stranger hands you by design.
 *
 * ⚠️ **So this reads a deliberately narrow subset**, and says so when it cannot: one sheet, cell
 * values, shared strings, inline strings. **No formulas** (the cached value is used, never
 * evaluated), no external references, no macros. Anything outside that produces a stated refusal
 * rather than a wrong answer.
 */
import type { ParsedCsv } from './csv.js';

/** What a caller gets when the file cannot be read. Never a partial parse. */
export class XlsxUnreadable extends Error {
  constructor(detail: string) {
    super(detail);
    this.name = 'XlsxUnreadable';
  }
}

/** `.xlsx` files begin with the local-file-header signature `PK\x03\x04`. */
export function looksLikeXlsx(bytes: Uint8Array): boolean {
  return bytes[0] === 0x50 && bytes[1] === 0x4b && bytes[2] === 0x03 && bytes[3] === 0x04;
}

interface ZipEntry {
  name: string;
  method: number;
  offset: number;
  compressedSize: number;
}

/**
 * Read the ZIP central directory.
 *
 * ⚠️ Parsed from the **end of the file**, not by walking local headers forward. The central
 * directory is the authoritative index; local headers can disagree with it, and a reader that
 * trusts them is the classic way a crafted archive smuggles one file past a check of another.
 */
function readCentralDirectory(view: DataView, bytes: Uint8Array): ZipEntry[] {
  // End-of-central-directory record: signature 0x06054b50, within the last 64KB (comment allowed).
  let eocd = -1;
  const from = Math.max(0, bytes.length - 65_557);
  for (let i = bytes.length - 22; i >= from; i -= 1) {
    if (view.getUint32(i, true) === 0x06054b50) {
      eocd = i;
      break;
    }
  }
  if (eocd < 0) throw new XlsxUnreadable('This does not look like a valid .xlsx file (no ZIP directory was found).');

  const count = view.getUint16(eocd + 10, true);
  let p = view.getUint32(eocd + 16, true);
  const entries: ZipEntry[] = [];

  for (let i = 0; i < count; i += 1) {
    if (view.getUint32(p, true) !== 0x02014b50) break;
    const method = view.getUint16(p + 10, true);
    const compressedSize = view.getUint32(p + 20, true);
    const nameLen = view.getUint16(p + 28, true);
    const extraLen = view.getUint16(p + 30, true);
    const commentLen = view.getUint16(p + 32, true);
    const offset = view.getUint32(p + 42, true);
    const name = new TextDecoder().decode(bytes.subarray(p + 46, p + 46 + nameLen));
    entries.push({ name, method, offset, compressedSize });
    p += 46 + nameLen + extraLen + commentLen;
  }
  return entries;
}

/**
 * Push bytes through a compression transform and collect the result.
 *
 * ⚠️ **Uses the transform's OWN `writable`/`readable` — it never constructs a `ReadableStream`.**
 * Two earlier shapes both hung for 5s per test on CI while passing locally in 21ms:
 * `new Response(stream).arrayBuffer()`, and piping a stream we made into the transform. The cause
 * is that under jsdom the global `ReadableStream` is jsdom's, while `DecompressionStream` is
 * Node's — `pipeThrough` across the two never settles, and it **hangs rather than throwing**,
 * which is the worst failure shape available.
 *
 * A transform's own two ends are the same implementation by construction, so this works in the
 * browser, in a Worker, and under the test environment without caring which globals are in play.
 */
async function through(transform: { writable: WritableStream; readable: ReadableStream }, bytes: Uint8Array): Promise<Uint8Array> {
  const writer = transform.writable.getWriter();
  // Not awaited before close(): a small payload can fill the queue and `write` only settles once
  // it drains, which for a transform that has not been read from yet is never.
  void writer.write(bytes.slice());
  void writer.close();

  const reader = transform.readable.getReader();
  const chunks: Uint8Array[] = [];
  let total = 0;
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    const chunk = value as Uint8Array;
    chunks.push(chunk);
    total += chunk.length;
  }
  const out = new Uint8Array(total);
  let at = 0;
  for (const c of chunks) {
    out.set(c, at);
    at += c.length;
  }
  return out;
}

async function inflate(bytes: Uint8Array): Promise<Uint8Array> {
  return through(new DecompressionStream('deflate-raw'), bytes);
}

async function readEntry(entry: ZipEntry, view: DataView, bytes: Uint8Array): Promise<string> {
  // The local header's variable-length fields tell us where the data actually starts.
  const nameLen = view.getUint16(entry.offset + 26, true);
  const extraLen = view.getUint16(entry.offset + 28, true);
  const start = entry.offset + 30 + nameLen + extraLen;
  const raw = bytes.subarray(start, start + entry.compressedSize);
  if (entry.method === 0) return new TextDecoder().decode(raw);
  if (entry.method === 8) return new TextDecoder().decode(await inflate(raw));
  throw new XlsxUnreadable(
    `This spreadsheet uses an unsupported compression method (${entry.method}). Re-save it as .xlsx or export CSV instead.`
  );
}

/** XML entity decoding, for the five that matter in spreadsheet text. */
function unescapeXml(value: string): string {
  return value
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&apos;/g, "'")
    /* ⚠️ `&amp;` LAST. Decoded first, an escaped `&amp;lt;` becomes `&lt;` and is then decoded
       again into `<` — a value silently different from what the file said. */
    .replace(/&amp;/g, '&');
}

/** Concatenated `<t>` runs inside one shared-string item; a styled cell splits into several. */
function textOf(itemXml: string): string {
  const parts = [...itemXml.matchAll(/<t[^>]*>([\s\S]*?)<\/t>/g)].map((m) => unescapeXml(m[1]!));
  return parts.join('');
}

function sharedStrings(xml: string): string[] {
  return [...xml.matchAll(/<si\b[^>]*>([\s\S]*?)<\/si>/g)].map((m) => textOf(m[1]!));
}

/** `A1` → 0, `B1` → 1, `AA1` → 26. Column letters are base-26 with no zero. */
export function columnIndex(ref: string): number {
  const letters = /^([A-Z]+)/.exec(ref)?.[1] ?? '';
  let n = 0;
  for (const ch of letters) n = n * 26 + (ch.charCodeAt(0) - 64);
  return n - 1;
}

/**
 * Turn a worksheet into rows of strings.
 *
 * ⚠️ **Gaps are preserved as empty cells, not skipped.** A spreadsheet omits empty cells
 * entirely, so a row `<c r="A1">…<c r="C1">` has no B. Appending in document order would shift
 * every later column left — the same class of silent corruption `parseCsv` reports as a ragged
 * row, and far worse here because it would look perfectly well-formed.
 */
function sheetRows(xml: string, strings: string[]): string[][] {
  const rows: string[][] = [];
  for (const rowMatch of xml.matchAll(/<row\b[^>]*>([\s\S]*?)<\/row>/g)) {
    const cells: string[] = [];
    for (const cell of rowMatch[1]!.matchAll(/<c\b([^>]*)>([\s\S]*?)<\/c>/g)) {
      const attrs = cell[1]!;
      const body = cell[2]!;
      const ref = /r="([A-Z]+\d+)"/.exec(attrs)?.[1];
      const index = ref ? columnIndex(ref) : cells.length;
      while (cells.length < index) cells.push('');

      const type = /t="([^"]+)"/.exec(attrs)?.[1];
      let value: string;
      if (type === 's') {
        const i = Number(/<v>([\s\S]*?)<\/v>/.exec(body)?.[1] ?? '-1');
        value = strings[i] ?? '';
      } else if (type === 'inlineStr') {
        value = textOf(body);
      } else {
        /* Includes a formula cell: `<f>` is ignored and the cached `<v>` is taken. Evaluating a
           formula would mean implementing a spreadsheet engine, and a stale cached value is at
           least what the person last saw on their own screen. */
        value = unescapeXml(/<v>([\s\S]*?)<\/v>/.exec(body)?.[1] ?? '');
      }
      cells[index] = value;
    }
    rows.push(cells);
  }
  return rows;
}

/**
 * Parse an `.xlsx` into the same shape `parseCsv` returns, so every downstream step —
 * fingerprinting, mapping, profiling, matching — is byte-identical between the two formats.
 *
 * That is the point of returning `ParsedCsv` rather than a richer type: one ingest path, whatever
 * the file was, exactly as `packages/connectors` does for transports.
 */
export async function parseXlsx(buffer: ArrayBuffer): Promise<ParsedCsv> {
  const bytes = new Uint8Array(buffer);
  if (!looksLikeXlsx(bytes)) {
    throw new XlsxUnreadable('This file is not an .xlsx workbook. Older .xls files are a different format — re-save as .xlsx or export CSV.');
  }
  const view = new DataView(buffer);
  const entries = readCentralDirectory(view, bytes);

  const sheets = entries.filter((e) => /^xl\/worksheets\/sheet\d+\.xml$/.test(e.name)).sort((a, b) => a.name.localeCompare(b.name));
  if (sheets.length === 0) throw new XlsxUnreadable('This workbook contains no worksheet.');

  const stringsEntry = entries.find((e) => e.name === 'xl/sharedStrings.xml');
  const strings = stringsEntry ? sharedStrings(await readEntry(stringsEntry, view, bytes)) : [];
  const rows = sheetRows(await readEntry(sheets[0]!, view, bytes), strings);

  const nonEmpty = rows.filter((r) => r.some((c) => c !== ''));
  if (nonEmpty.length === 0) throw new XlsxUnreadable('The first sheet of this workbook is empty.');

  const [headerRow, ...dataRows] = nonEmpty;
  const headers = headerRow!.map((h) => h.trim());

  /* Ragged rows are counted the same way `parseCsv` counts them, and for the same reason: a row
     that does not match the header usually means the data is *wrong* rather than merely missing,
     and a visitor needs to know before trusting a duplicate count derived from it. */
  let raggedRows = 0;
  const normalised = dataRows.map((row) => {
    if (row.length !== headers.length) raggedRows += 1;
    const out = [...row];
    while (out.length < headers.length) out.push('');
    return out.slice(0, headers.length);
  });

  return {
    headers,
    rows: normalised,
    raggedRows,
    /* A workbook has no delimiter. Reported honestly rather than as a fake ',' — the mapping
       screen shows this, and telling someone their Excel file is comma-delimited is a small lie
       that makes the screen untrustworthy about the things that matter. */
    delimiter: 'none (spreadsheet)',
  };
}
